import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { EmptyState, Widget } from '@heroui-pro/react';
import { BarChart } from '@heroui-pro/react/bar-chart';
import type { TopCategories } from '@/api/models';
import { formatCurrencyValue } from '@/utils/currency';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { CategoryAnalysisTooltip } from './CategoryAnalysisTooltip';
import {
    BAR_CHART_HEIGHT_PX,
    Y_AXIS_WIDTH_PX,
    toChartRows,
} from './categoryAnalysisMeta';

type CategoryAnalysisViewProps = {
    data: TopCategories;
};

export const CategoryAnalysisView: React.FC<CategoryAnalysisViewProps> = ({
    data,
}) => {
    const { t } = useTranslation('pages');
    const { t: tComponents } = useTranslation('components');

    const isEmpty = data.categories.length === 0;

    const copy = React.useMemo(
        () => ({
            title: t('sales.widgets.categoryAnalysis.title'),
            subtitle: t('sales.widgets.categoryAnalysis.subtitle'),
            emptyTitle: t('sales.widgets.categoryAnalysis.empty.title'),
            emptyDescription: t(
                'sales.widgets.categoryAnalysis.empty.description',
            ),
        }),
        [t],
    );

    const formatRevenue = React.useCallback(
        (value: number | string) => formatCurrencyValue(data.currency, value),
        [data.currency],
    );

    return (
        <section
            aria-labelledby="category-analysis-widget-title"
            {...getNodeTestIdProp('category-analysis-section')}
        >
            <Widget
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('category-analysis-widget')}
            >
                <Widget.Header
                    className="mt-4"
                    {...getNodeTestIdProp('category-analysis-widget-header')}
                >
                    <Widget.Title
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('category-analysis-widget-title')}
                    >
                        <div
                            aria-hidden
                            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent"
                            {...getNodeTestIdProp(
                                'category-analysis-icon-wrap',
                            )}
                        >
                            <Icon
                                className="size-4"
                                icon="solar:chart-square-bold-duotone"
                                {...getNodeTestIdProp('category-analysis-icon')}
                            />
                        </div>
                        {copy.title}
                    </Widget.Title>
                    <Widget.Description
                        {...getNodeTestIdProp(
                            'category-analysis-widget-subtitle',
                        )}
                    >
                        {copy.subtitle}
                    </Widget.Description>
                </Widget.Header>
                <Widget.Content
                    className={
                        isEmpty
                            ? 'flex min-h-[220px] items-center justify-center'
                            : undefined
                    }
                >
                    {isEmpty ? (
                        <EmptyState
                            {...getNodeTestIdProp('category-analysis-empty')}
                        >
                            <EmptyState.Header>
                                <EmptyState.Media variant="icon">
                                    <Icon
                                        aria-hidden
                                        className="size-6"
                                        icon="solar:chart-square-bold-duotone"
                                        {...getNodeTestIdProp(
                                            'category-analysis-empty-icon',
                                        )}
                                    />
                                </EmptyState.Media>
                                <EmptyState.Title
                                    {...getNodeTestIdProp(
                                        'category-analysis-empty-title',
                                    )}
                                >
                                    {copy.emptyTitle}
                                </EmptyState.Title>
                                <EmptyState.Description
                                    {...getNodeTestIdProp(
                                        'category-analysis-empty-description',
                                    )}
                                >
                                    {copy.emptyDescription}
                                </EmptyState.Description>
                            </EmptyState.Header>
                        </EmptyState>
                    ) : (
                        <div
                            aria-label={copy.title}
                            role="img"
                            style={{ height: BAR_CHART_HEIGHT_PX }}
                            {...getNodeTestIdProp('category-analysis-chart')}
                        >
                            <BarChart
                                data={
                                    toChartRows(
                                        data.categories,
                                        tComponents('deletedMarker'),
                                        tComponents('refLabel.unknown'),
                                    ) as unknown as Record<
                                        string,
                                        number | string
                                    >[]
                                }
                                height={BAR_CHART_HEIGHT_PX}
                                layout="vertical"
                                margin={{
                                    bottom: 8,
                                    left: 8,
                                    right: 16,
                                    top: 8,
                                }}
                                width="100%"
                            >
                                <BarChart.Grid
                                    horizontal={false}
                                    stroke="var(--surface-tertiary)"
                                    strokeDasharray="3 3"
                                />
                                <BarChart.XAxis
                                    axisLine={false}
                                    hide
                                    type="number"
                                />
                                <BarChart.YAxis
                                    axisLine={false}
                                    dataKey="categoryName"
                                    tick={{
                                        fill: 'var(--muted)',
                                        fontSize: 12,
                                    }}
                                    tickLine={false}
                                    type="category"
                                    width={Y_AXIS_WIDTH_PX}
                                />
                                <BarChart.Tooltip
                                    content={
                                        <CategoryAnalysisTooltip
                                            valueFormatter={formatRevenue}
                                        />
                                    }
                                    cursor={{
                                        fill: 'var(--surface-secondary)',
                                    }}
                                />
                                <BarChart.Bar
                                    dataKey="totalRevenue"
                                    fill="var(--accent)"
                                    isAnimationActive={false}
                                    radius={[0, 4, 4, 0]}
                                />
                            </BarChart>
                        </div>
                    )}
                </Widget.Content>
            </Widget>
        </section>
    );
};
